import React from 'react';
import { Slider } from '../../../components/Slider';
import { ColorPicker } from '../../../components/ColorPicker';
import { CollapsibleSection } from '../../../components/CollapsibleSection';
import { RAW, FONT_SIZE, SPACE } from '../../../theme';
import {
  TabProps, SectionLabel, SelectBox, ActionButton, TagBadge,
  PrimitiveRow, PrimitiveHeader, TypeBadge, ButtonRow,
  BEHAVIOR_TYPES, BUFF_STATS, ACCENT, FIELD_LABEL, REMOVE_RED, COUNTER_GREY,
} from './shared';

export const BehaviorTab: React.FC<TabProps> = ({ ability, update }) => {
  const tags = ability.tags || [];
  const types: string[] = [];
  if (ability.projectile) types.push('Projectile');
  if (tags.includes('aoe')) types.push('AOE');
  if (tags.includes('buff') || tags.includes('defensive')) types.push('Buff');
  if (tags.includes('heal')) types.push('Heal');
  if (tags.includes('movement')) types.push('Dash');
  if (tags.includes('channel')) types.push('Channel');
  if (tags.includes('debuff')) types.push('Debuff');
  if (tags.includes('melee')) types.push('Melee');
  if (tags.includes('execute')) types.push('Execute');
  if (tags.includes('stealth')) types.push('Stealth');
  if (tags.includes('trap')) types.push('Trap');
  if (tags.includes('poison')) types.push('Poison');
  if (types.length === 0) types.push('Instant');

  const m = ability.melee || {};
  const patchMelee = (patch: any) => update({ melee: { ...ability.melee, ...patch } });

  const layers = ability.buffLayers || [];
  const patchLayers = (newLayers: any[]) => update({ buffLayers: newLayers });

  const behaviors = ability.behaviors || [];
  const patchBehaviors = (newBehaviors: any[]) => update({ behaviors: newBehaviors });

  return (
    <>
      <SectionLabel>Active Behaviors</SectionLabel>
      <div style={{ marginBottom: SPACE.md, display: 'flex', flexWrap: 'wrap', gap: SPACE.xs }}>
        {types.map(t => <TypeBadge key={t}>{t}</TypeBadge>)}
      </div>

      {/* Behavior stacking — add/remove behavior slots */}
      <CollapsibleSection title="Behavior Stack" badge={`${behaviors.length || types.length}`} defaultOpen={behaviors.length > 0}>
        {behaviors.map((b, i) => (
          <PrimitiveRow key={i}>
            <PrimitiveHeader>
              <TypeBadge>{b.type}</TypeBadge>
              <span style={{ fontSize: FONT_SIZE.xs, color: COUNTER_GREY }}>#{i}</span>
              <span style={{ marginLeft: 'auto', cursor: 'pointer', color: REMOVE_RED, fontSize: FONT_SIZE.md }}
                onClick={() => patchBehaviors(behaviors.filter((_, j) => j !== i))}>✕</span>
            </PrimitiveHeader>
            {b.params && Object.entries(b.params).map(([k, v]) => (
              <div key={k} style={{ fontSize: FONT_SIZE.xs, color: RAW.silver, display: 'flex', justifyContent: 'space-between' }}>
                <span>{k}</span><span style={{ color: ACCENT }}>{String(v)}</span>
              </div>
            ))}
          </PrimitiveRow>
        ))}
        <ButtonRow>
          <SelectBox id="add-behavior-type" style={{ marginBottom: 0, flex: 1 }}>
            {BEHAVIOR_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
          </SelectBox>
          <ActionButton $variant="green" style={{ width: 'auto', padding: '5px 12px', marginBottom: 0 }}
            onClick={() => {
              const sel = (document.getElementById('add-behavior-type') as HTMLSelectElement)?.value || 'melee';
              patchBehaviors([...behaviors, { type: sel as any, params: {} }]);
            }}>+ Add</ActionButton>
        </ButtonRow>
      </CollapsibleSection>

      {/* Melee Swing Editor — visual + gameplay controls */}
      <CollapsibleSection title="Melee Swing" badge={m.arcRadius ? `${m.arcRadius}px` : undefined}
        defaultOpen={tags.includes('melee')}>
        <SectionLabel>Visual</SectionLabel>
        <Slider label="Arc Radius (px)" value={m.arcRadius ?? 60} min={20} max={200} step={5}
          onChange={v => patchMelee({ arcRadius: v })} />
        <Slider label="Arc Spread (°)" value={m.arcSpread ?? 90} min={15} max={360} step={15}
          formatValue={v => `${v}°`} onChange={v => patchMelee({ arcSpread: v })} />
        <Slider label="Arc Start Angle (°)" value={m.arcAngle ?? -45} min={-180} max={180} step={15}
          formatValue={v => `${v}°`} onChange={v => patchMelee({ arcAngle: v })} />
        <Slider label="Swing Duration (ms)" value={m.swingDuration ?? 150} min={50} max={500} step={25}
          onChange={v => patchMelee({ swingDuration: v })} />
        <Slider label="Line Width" value={m.lineWidth ?? 4} min={1} max={12} step={1}
          onChange={v => patchMelee({ lineWidth: v })} />
        <ColorPicker label="Swing Color" value={m.lineColor ?? 0xFF3333}
          onChange={v => patchMelee({ lineColor: v })} />

        <SectionLabel>Gameplay</SectionLabel>
        <Slider label="Hit Range (px)" value={m.hitRange ?? 60} min={20} max={200} step={5}
          onChange={v => patchMelee({ hitRange: v })} />
        <Slider label="Knockback (px)" value={m.knockback ?? 0} min={0} max={200} step={10}
          onChange={v => patchMelee({ knockback: v })} />

        {/* Screen shake is tier-owned (plan 29 F8): a melee swing cannot author one. */}
      </CollapsibleSection>

      {/* Buff/Aura Layer stacking */}
      <CollapsibleSection title="Buff / Aura Layers" badge={layers.length > 0 ? `${layers.length}` : undefined}
        defaultOpen={layers.length > 0}>
        {layers.map((layer, i) => (
          <PrimitiveRow key={i}>
            <PrimitiveHeader>
              <TypeBadge $color="rgba(16,185,129,0.2)">Buff #{i}</TypeBadge>
              <span style={{ marginLeft: 'auto', cursor: 'pointer', color: REMOVE_RED, fontSize: FONT_SIZE.md }}
                onClick={() => patchLayers(layers.filter((_, j) => j !== i))}>✕</span>
            </PrimitiveHeader>
            <div style={{ fontSize: FONT_SIZE.xs, color: FIELD_LABEL, marginBottom: 3 }}>Stat</div>
            <SelectBox value={layer.stat} onChange={e => {
              const newLayers = [...layers];
              newLayers[i] = { ...layer, stat: e.target.value };
              patchLayers(newLayers);
            }}>
              {BUFF_STATS.map(s => <option key={s} value={s}>{s}</option>)}
            </SelectBox>
            <Slider label="Value" value={layer.value} min={-50} max={50} step={1}
              onChange={v => { const nl = [...layers]; nl[i] = { ...layer, value: v }; patchLayers(nl); }} />
            <Slider label="Duration (ms)" value={layer.duration} min={1000} max={60000} step={1000}
              formatValue={v => `${(v / 1000).toFixed(0)}s`}
              onChange={v => { const nl = [...layers]; nl[i] = { ...layer, duration: v }; patchLayers(nl); }} />
          </PrimitiveRow>
        ))}
        <ActionButton $variant="green" onClick={() => patchLayers([...layers, { stat: 'attackBonus', value: 5, duration: 10000 }])}>
          + Add Buff Layer
        </ActionButton>
      </CollapsibleSection>

      <CollapsibleSection title="Info">
        <div style={{ fontSize: FONT_SIZE.xs, color: ACCENT, fontFamily: 'monospace', marginBottom: SPACE.sm }}>{ability.id}</div>
        <div style={{ fontSize: FONT_SIZE.xs, color: RAW.gray, display: 'flex', flexWrap: 'wrap', gap: 3 }}>
          {tags.map(t => <TagBadge key={t}>{t}</TagBadge>)}
        </div>
        <div style={{ fontSize: FONT_SIZE.xs, color: '#ccc', lineHeight: 1.4, marginTop: SPACE.sm }}>
          {ability.description || 'No description.'}
        </div>
      </CollapsibleSection>
    </>
  );
};
